import { CSSProperties, useEffect, useState } from 'react';
import { HERO_VIDEO_SRC, heroSlides } from '../../data/home';
import { ArrowLink, OrderLink } from './links';
import useFitText from './useFitText';

const DURATION = 6000;
const DESKTOP = '(min-width: 900px)';

const useMedia = (query: string) => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
};

const SpHero = () => {
  const desktop = useMedia(DESKTOP);
  const reduced = useMedia('(prefers-reduced-motion: reduce)');
  const [index, setIndex] = useState(0);
  const [previous, setPrevious] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const [runKey, setRunKey] = useState(0);
  const wordmark = useFitText<HTMLHeadingElement>();
  const hasVideo = Boolean(HERO_VIDEO_SRC);

  // Pause while the tab is hidden so returning doesn't land mid-skip.
  const [hidden, setHidden] = useState(document.hidden);
  useEffect(() => {
    const onVis = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    if (hasVideo || paused || hidden) return;
    const id = window.setTimeout(() => {
      setPrevious(index);
      setIndex((i) => (i + 1) % heroSlides.length);
      setRunKey((k) => k + 1);
    }, DURATION);
    return () => window.clearTimeout(id);
  }, [index, paused, hidden, hasVideo, runKey]);

  useEffect(() => {
    if (previous === null) return;
    const id = window.setTimeout(() => setPrevious(null), 2000);
    return () => window.clearTimeout(id);
  }, [previous]);

  const togglePause = () => {
    setPaused((p) => !p);
    setRunKey((k) => k + 1);
  };

  const current = heroSlides[index];
  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    <section className="sp-hero" id="hero" aria-label="Thaprachan Thai Cuisine">
      <div className="sp-hero-media">
        {hasVideo ? (
          <video
            className="sp-hero-video"
            muted
            autoPlay={!reduced}
            loop
            playsInline
            poster={desktop ? heroSlides[0].desktop : heroSlides[0].mobile}
          >
            <source src={HERO_VIDEO_SRC ?? undefined} />
          </video>
        ) : (
          heroSlides.map((slide, i) => (
            <figure
              key={slide.desktop}
              className={`sp-slide${i === index ? ' is-on' : ''}${i === previous ? ' was-on' : ''}`}
              aria-hidden={i !== index}
            >
              <picture>
                <source media={DESKTOP} srcSet={slide.desktop} />
                <img
                  src={slide.mobile}
                  alt={desktop ? slide.alt.desktop : slide.alt.mobile}
                  decoding="async"
                  // The first photo is the page's largest paint; the rest wait their turn.
                  {...(i === 0 ? { fetchpriority: 'high' } : { loading: 'lazy' })}
                  style={{ '--pos-d': slide.posD ?? '50% 50%', '--pos-m': slide.posM ?? '50% 50%' } as CSSProperties}
                />
              </picture>
            </figure>
          ))
        )}
      </div>

      <div className="sp-hero-foot">
        <div className="sp-hero-row">
          <div>
            <p className="sp-hero-line">
              <span>Authentic Thai Cuisine</span>
            </p>
            <div className="sp-hero-ctas">
              <OrderLink />
              <ArrowLink to="/menu" track="menu_click">View Menu</ArrowLink>
            </div>
          </div>

          {!hasVideo && (
            <div className="sp-counter">
              <div className="sp-counter-top">
                <span className="sp-num">
                  {pad(index + 1)} / {pad(heroSlides.length)}
                </span>
                <span className="sp-counter-name">{desktop ? current.label.desktop : current.label.mobile}</span>
                <button
                  type="button"
                  className="sp-pause"
                  aria-label={paused ? 'Play slideshow' : 'Pause slideshow'}
                  onClick={togglePause}
                >
                  <svg viewBox="0 0 14 14" aria-hidden="true">
                    {paused ? (
                      <path d="M3 1.5l9 5.5-9 5.5z" />
                    ) : (
                      <>
                        <rect x="2.5" y="1.5" width="2.5" height="11" />
                        <rect x="9" y="1.5" width="2.5" height="11" />
                      </>
                    )}
                  </svg>
                </button>
              </div>
              <div
                className={`sp-progress${paused ? ' is-paused' : ''}`}
                style={{ '--n': heroSlides.length, '--dur': `${DURATION}ms` } as CSSProperties}
                aria-hidden="true"
              >
                {heroSlides.map((slide, i) => (
                  <i
                    key={i === index ? `${slide.desktop}-${runKey}` : slide.desktop}
                    className={i < index ? 'is-done' : i === index ? 'is-run' : undefined}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <h1 className="sp-wordmark" ref={wordmark}>
          <span>Thaprachan</span>
          <span className="sp-sr"> Thai Cuisine, Thai restaurant in Seattle near UW</span>
        </h1>
      </div>
    </section>
  );
};

export default SpHero;
